import { useState } from 'react'
import {
  Copy,
  Edit2,
  Loader2,
  MoreVertical,
  Pause,
  Phone,
  Play,
  Plus,
  Trash2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import {
  useCreateCampaign,
  useDeleteCampaign,
  usePowerDialerCampaigns,
  type PowerDialerCampaignSummary,
} from '@/api/powerDialer'
import { cn } from '@/lib/utils'
import { usePlanRights } from '@/hooks/usePlanRights'
import { PlanBadge } from '@/components/ui/PlanBadge'

interface Props {
  onCreate: () => void
  onEdit: (id: number) => void
}

const APERCU_CAMPAGNES: PowerDialerCampaignSummary[] = [
  { id: -1, name: 'Relance devis septembre', description: '', status: 'active', total_prospects: 240, prospects_called: 163, prospects_interested: 31, agents_count: 3, numbers_count: 2, created_at: '2026-09-08T14:00:00Z' },
  { id: -2, name: 'Anciens clients 2025', description: '', status: 'paused', total_prospects: 512, prospects_called: 388, prospects_interested: 57, agents_count: 2, numbers_count: 1, created_at: '2026-08-21T14:00:00Z' },
  { id: -3, name: 'Salon de l\u2019auto', description: '', status: 'completed', total_prospects: 96, prospects_called: 96, prospects_interested: 19, agents_count: 4, numbers_count: 2, created_at: '2026-07-30T14:00:00Z' },
]

export function CampaignListView({ onCreate, onEdit }: Props) {
  const { allowed, planFor } = usePlanRights()
  const pdPermis = allowed('power_dialer')
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { data: vraies = [], isLoading } = usePowerDialerCampaigns()
  // Sans le plan et sans campagne : on montre un aperçu flouté de ce que
  // donne la page, plutôt qu'un écran vide.
  const apercu = !pdPermis && vraies.length === 0
  const campaigns = apercu
    ? APERCU_CAMPAGNES.map((c) => ({
        ...c,
        name: t(`manager.powerDialer.list.preview.c${-c.id}`, c.name),
      }))
    : vraies
  const deleteCampaign = useDeleteCampaign()
  const createCampaign = useCreateCampaign()

  // PATCH générique pour basculer le statut sans instancier 1 mutation par row.
  const toggleStatus = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      await api.patch(`/api/power-dialer/campaigns/${id}/`, { status })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['power-dialer-campaigns'] })
    },
  })

  const [pendingDelete, setPendingDelete] = useState<PowerDialerCampaignSummary | null>(null)

  const statusLabel = (status: string) =>
    t(`manager.powerDialer.list.status.${status}`, {
      defaultValue: status,
    })

  const statusVariant = (
    status: string,
  ): 'default' | 'secondary' | 'outline' => {
    if (status === 'active') return 'default'
    if (status === 'ready') return 'outline'
    return 'secondary'
  }

  const handleToggleStatus = async (campaign: PowerDialerCampaignSummary) => {
    const next = campaign.status === 'active' ? 'paused' : 'active'
    try {
      await toggleStatus.mutateAsync({ id: campaign.id, status: next })
      toast.success(
        t(
          next === 'active'
            ? 'manager.powerDialer.list.toast.resumed'
            : 'manager.powerDialer.list.toast.paused',
        ),
      )
    } catch {
      toast.error(t('manager.powerDialer.list.toast.statusError'))
    }
  }

  const handleDuplicate = async (campaign: PowerDialerCampaignSummary) => {
    try {
      await createCampaign.mutateAsync({
        name: t('manager.powerDialer.list.copyName', { name: campaign.name }),
        description: campaign.description,
        status: 'ready',
      })
      toast.success(t('manager.powerDialer.list.toast.duplicated'))
    } catch {
      toast.error(t('manager.powerDialer.list.toast.duplicateError'))
    }
  }

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return
    try {
      await deleteCampaign.mutateAsync(pendingDelete.id)
      toast.success(t('manager.powerDialer.list.toast.deleted'))
      setPendingDelete(null)
    } catch {
      toast.error(t('manager.powerDialer.list.toast.deleteError'))
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">
            {t('manager.powerDialer.sectionTitle')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('manager.powerDialer.list.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {!pdPermis && <PlanBadge plan={planFor('power_dialer')} />}
          <Button
            type="button"
            onClick={onCreate}
            disabled={!pdPermis}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('manager.powerDialer.list.newCampaign')}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-12 text-center">
          <Phone className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
            {t('manager.powerDialer.list.emptyTitle')}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
            {t('manager.powerDialer.list.emptySubtitle')}
          </p>
          <div className="flex flex-col items-center gap-3">
          {!pdPermis && <PlanBadge plan={planFor('power_dialer')} />}
          <Button
            type="button"
            onClick={onCreate}
            disabled={!pdPermis}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('manager.powerDialer.list.createFirst')}
          </Button>
        </div>
        </div>
      ) : (
        <div className="relative">
        {apercu && (
          <div className="absolute inset-0 z-10 flex items-start justify-center pt-16">
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-10 py-8 text-center shadow-xl">
              <Phone className="w-10 h-10 mx-auto mb-3 text-blue-500" />
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                {t('plan.required', { plan: 'Business' })}
              </p>
              <PlanBadge plan={planFor('power_dialer')} />
            </div>
          </div>
        )}
        <div
          className={cn(
            'rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden',
            apercu && 'blur-[3px] opacity-70 pointer-events-none select-none',
          )}
          aria-hidden={apercu || undefined}
        >
          <table className="w-full text-left border-collapse">
            <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
              <tr>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('manager.powerDialer.list.table.campaign')}
                </th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('manager.powerDialer.list.table.status')}
                </th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('manager.powerDialer.list.table.progress')}
                </th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('manager.powerDialer.list.table.agents')}
                </th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('manager.powerDialer.list.table.interested')}
                </th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {campaigns.map((campaign) => {
                const progress =
                  campaign.total_prospects > 0
                    ? Math.round(
                        (campaign.prospects_called / campaign.total_prospects) *
                          100,
                      )
                    : 0
                return (
                  <tr
                    key={campaign.id}
                    onClick={() => onEdit(campaign.id)}
                    className="group hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-3">
                      <span className="text-sm font-medium text-slate-900 dark:text-white">
                        {campaign.name}
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      <Badge variant={statusVariant(campaign.status)}>
                        {statusLabel(campaign.status)}
                      </Badge>
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-3 w-40">
                        <div className="flex-1 h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-600 rounded-full transition-all"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-500 dark:text-slate-400 w-16 text-right tabular-nums">
                          {campaign.prospects_called}/{campaign.total_prospects}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-3 text-sm text-slate-600 dark:text-slate-400">
                      {campaign.agents_count}
                    </td>
                    <td className="px-6 py-3">
                      <span className="text-sm font-medium text-green-600 dark:text-green-400">
                        {campaign.prospects_interested}
                      </span>
                    </td>
                    <td
                      className="px-6 py-3 text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem onClick={() => onEdit(campaign.id)}>
                            <Edit2 className="w-4 h-4 mr-2" />
                            {t('manager.powerDialer.list.actions.edit')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => void handleToggleStatus(campaign)}
                          >
                            {campaign.status === 'active' ? (
                              <Pause className="w-4 h-4 mr-2" />
                            ) : (
                              <Play className="w-4 h-4 mr-2" />
                            )}
                            {campaign.status === 'active'
                              ? t('manager.powerDialer.list.actions.pause')
                              : t('manager.powerDialer.list.actions.resume')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => void handleDuplicate(campaign)}
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            {t('manager.powerDialer.list.actions.duplicate')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setPendingDelete(campaign)}
                            className="text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            {t('manager.powerDialer.list.actions.delete')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        </div>
      )}

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('manager.powerDialer.list.deleteDialog.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('manager.powerDialer.list.deleteDialog.description', {
                name: pendingDelete?.name,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteCampaign.isPending}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                void handleConfirmDelete()
              }}
              disabled={deleteCampaign.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {t('manager.powerDialer.list.deleteDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
